import { create } from "zustand";
import {
  ModerationQueueFlag,
  ModerationQueuePage,
  ModerationReviewResource,
  ModerationReviewStatus,
  ModerationFlag,
  ModerationConfigResponse,
  Blocklist,
  Allowlist,
  ModerationResource,
  ModerationAction,
} from "../api/moderation";

interface ModerationState {
  flags: ModerationFlag[];
  configResponse: ModerationConfigResponse | null;
  blocklist: Blocklist | null;
  allowlist: Allowlist | null;
  loadingFlags: boolean;
  loadingConfig: boolean;
  loadingBlocklist: boolean;
  loadingAllowlist: boolean;
  error: string | null;
  loadFlags: (params?: {
    meetingId?: string;
    action?: ModerationAction;
    limit?: number;
    nextToken?: string;
  }) => Promise<void>;
  /**
   * The review queue: pending flags first, then decided ones, each group
   * newest first.
   */
  reviewFlags: ModerationQueueFlag[];
  loadingReviewFlags: boolean;
  /**
   * Set when the queue has more pages than were fetched.
   *
   * The grid these rows go into filters and pages client-side, so it cannot ask
   * for more — this is surfaced in the UI instead, so a moderator is told the
   * page is partial rather than shown a short list and left to assume there is
   * nothing left to do.
   */
  reviewNextToken?: string;
  /**
   * Errors from the review queue alone.
   *
   * Separate from `error`, which four other loaders write: a failed blocklist
   * read would otherwise surface as a red banner over a perfectly healthy
   * queue, and stay there.
   */
  reviewError: string | null;
  /**
   * Which status the queue is currently showing.
   *
   * Held here rather than in `ImageReviewList` because the refresh button lives
   * in the parent screen, which would otherwise reload PENDING while the list
   * below it still says Rejected — a refresh that silently changes the view.
   */
  reviewStatus: ReviewStatusFilter;
  /**
   * Resolves with what became of the read, so a caller reporting on it can
   * tell a success from one that was superseded and never landed.
   */
  loadReviewFlags: (
    status?: ReviewStatusFilter
  ) => Promise<"ok" | "error" | "superseded">;
  loadConfig: () => Promise<void>;
  loadBlocklist: () => Promise<void>;
  loadAllowlist: () => Promise<void>;
}

/**
 * What the status control can be set to.
 *
 * `ALL` is a UI concept with no request behind it: `status` is the partition
 * key of the index this reads, so there is no query meaning "every status".
 * The store fans out to one read per status instead — see `loadReviewFlags`.
 */
export type ReviewStatusFilter = ModerationReviewStatus | "ALL";

/** The statuses `ALL` expands to, newest-first once merged. */
const ALL_STATUSES: ModerationReviewStatus[] = [
  "PENDING",
  "APPROVED",
  "REJECTED",
];

/**
 * Which review-queue read is the current one.
 *
 * Two reads can be in flight at once — a moderator picking Approved and then
 * Rejected before the first lands — and each writes `reviewFlags` on resolve.
 * Without this, the slower response wins and the grid holds one status while
 * the control says another, which the client-side filter then renders as an
 * empty archive rather than an error. Module-scoped like the store itself.
 */
let latestReviewRead = 0;

export const useModerationStore = create<ModerationState>((set) => ({
  flags: [],
  reviewFlags: [],
  loadingReviewFlags: false,
  reviewNextToken: undefined,
  reviewError: null,
  reviewStatus: "ALL",
  configResponse: null,
  blocklist: null,
  allowlist: null,
  loadingFlags: false,
  loadingConfig: false,
  loadingBlocklist: false,
  loadingAllowlist: false,
  error: null,

  loadFlags: async (params?) => {
    set({ loadingFlags: true, error: null });
    try {
      const response = await ModerationResource.getFlags(params);
      set({ flags: response.data, loadingFlags: false });
    } catch (err) {
      const error = err instanceof Error ? err.message : "Failed to load moderation flags";
      set({ error, loadingFlags: false });
    }
  },

  loadReviewFlags: async (status = "ALL") => {
    // Recorded before the await, not after: a failed read must still leave the
    // store agreeing with the control the moderator just moved, or the next
    // refresh would quietly fetch the status they navigated away from.
    //
    // The rows go with it. Keeping the previous status's rows while the status
    // says otherwise is what makes a stale list readable as a fresh one — and
    // the status-aware copy would then describe pending rows as rejected.
    const read = ++latestReviewRead;
    set({
      loadingReviewFlags: true,
      reviewError: null,
      reviewStatus: status,
      reviewFlags: [],
      reviewNextToken: undefined,
    });
    try {
      // Asked for by kind, not fetched unfiltered and sieved here. Chat flags
      // share this table and this index, and chat is by far the highest-volume
      // writer — so one unnarrowed page of 100 can be entirely chat, leaving
      // nothing to show while pictures sit unreviewed a page back. The endpoint
      // pages a narrowed read until it fills the limit, which is exactly this
      // case; the extra requests are the price of the queue being truthful.
      //
      // One read per (status, kind): two for a single status, six for `ALL`,
      // which has no request of its own because status is the partition key.
      // Worth knowing before adding a third kind or a fourth status.
      const statuses = status === "ALL" ? ALL_STATUSES : [status];
      // `allSettled`, not `all`. Under ALL this is six reads, and `all`
      // rejects on the first failure — so a 500 on the APPROVED archive would
      // blank the pending work the screen exists for. Show what came back and
      // say what did not.
      const settled = await Promise.allSettled(
        statuses.flatMap((each) =>
          (["PROFILE_PICTURE", "USER"] as const).map((contentType) =>
            ModerationReviewResource.getQueue({
              status: each,
              contentType,
              limit: 50,
            })
          )
        )
      );

      if (read !== latestReviewRead) return "superseded";

      const pages: ModerationQueuePage[] = settled
        .filter(
          (result): result is PromiseFulfilledResult<ModerationQueuePage> =>
            result.status === "fulfilled"
        )
        .map((result) => result.value);
      const failures = settled.filter((result) => result.status === "rejected");

      // Nothing came back: that is an error, not an empty queue.
      if (pages.length === 0) {
        const reason = (failures[0] as PromiseRejectedResult | undefined)
          ?.reason;
        set({
          reviewError:
            reason instanceof Error
              ? reason.message
              : "Failed to load the review queue",
          loadingReviewFlags: false,
        });
        return "error";
      }

      // Work first, record second. Under `ALL` the decided rows outnumber the
      // pending ones and are usually newer — the archive grows every time a
      // moderator acts — so a plain newest-first sort would bury the only rows
      // that still need somebody. Within each group, newest first as before.
      const merged = pages.flatMap((page) => page.data).sort((a, b) => {
        const aPending = a.status === "PENDING" ? 0 : 1;
        const bPending = b.status === "PENDING" ? 0 : 1;
        if (aPending !== bPending) return aPending - bPending;
        return b.createdAt.localeCompare(a.createdAt);
      });

      set({
        // Partial reads are reported rather than hidden: a queue missing a
        // third of itself must not look complete.
        reviewError: failures.length
          ? `Showing a partial queue — ${failures.length} of ${settled.length} reads failed.`
          : null,
        reviewFlags: merged,
        // Any page having more says the view is partial.
        reviewNextToken:
          pages.find((page) => page.paging.nextToken)?.paging.nextToken ??
          undefined,
        loadingReviewFlags: false,
      });
      return failures.length ? "error" : "ok";
    } catch (err) {
      if (read !== latestReviewRead) return "superseded";

      const reviewError =
        err instanceof Error ? err.message : "Failed to load the review queue";
      set({ reviewError, loadingReviewFlags: false });
      return "error";
    }
  },

  loadConfig: async () => {
    set({ loadingConfig: true, error: null });
    try {
      const response = await ModerationResource.getConfig();
      set({ configResponse: response, loadingConfig: false });
    } catch (err) {
      const error = err instanceof Error ? err.message : "Failed to load moderation config";
      set({ error, loadingConfig: false });
    }
  },

  loadBlocklist: async () => {
    set({ loadingBlocklist: true, error: null });
    try {
      const response = await ModerationResource.getBlocklist();
      set({ blocklist: response, loadingBlocklist: false });
    } catch (err) {
      const error = err instanceof Error ? err.message : "Failed to load blocklist";
      set({ error, loadingBlocklist: false });
    }
  },

  loadAllowlist: async () => {
    set({ loadingAllowlist: true, error: null });
    try {
      const response = await ModerationResource.getAllowlist();
      set({ allowlist: response, loadingAllowlist: false });
    } catch (err) {
      const error = err instanceof Error ? err.message : "Failed to load allowlist";
      set({ error, loadingAllowlist: false });
    }
  },
}));
